import React, { useCallback, useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import {
    WizardPage,
    wizardProps
} from '@xengage/gw-portals-wizard-react';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { Claim } from 'gw-capability-fnol-common-react';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { withRouter } from 'react-router-dom';
import { ProductUtil } from 'wnice-portals-util-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import metadata from './FNOLCPPSelectLinePage.metadata.json5';
import messages from '../../FNOLCPP.messages';
import wizardConfig from '../../config/fnol-cpp-wizard-config.json5';

function FNOLCPPSelectLinePage(props) {
    const {
        wizardData: claimVM,
        wizardSnapshot,
        updateWizardData,
        authHeader,
        changeNextSteps,
        currentStepIndex,
        steps
    } = props;
    const translator = useTranslator();
    const modalApi = useModal();
    const { FNOLService } = useDependencies('FNOLService');
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLCPPSelectLinePage');

    const { steps: wizardSteps } = wizardConfig;

    const {
        lossDate,
        claimNumber,
        policy: {
            policyNumber,
            lobCode_Ext: lobCode
        },
        policies: cppPolicies = []
    } = claimVM.value;

    const previousLine = _.get(wizardSnapshot, 'value.policy.lobCode_Ext');
    
    const [lineSelected, setLineSelected] = useState(lobCode);
    const [availableLines, setAvailableLines] = useState([]);

    const hasLineChanged = useCallback(() => {
        return previousLine !== lineSelected;
    }, [lineSelected, previousLine]);

    const onLineChange = useCallback((value) => {
        setLineSelected(value);
        const newClaimVM = viewModelService.clone(claimVM);
        const policySelected = _.find(cppPolicies, (pol) => pol.lobCode_Ext === value);
        _.set(newClaimVM.value, 'policy', policySelected);
        updateWizardData(newClaimVM);
    }, [claimVM, cppPolicies, updateWizardData, viewModelService]);

    const isSelectedLine = useCallback(() => {
        return !_.isEmpty(lineSelected);
    }, [lineSelected]);

    const getLinesDropdown = useCallback((lobCodes) => {
        return _.map(lobCodes, (code) => {
            let lineName;
            switch(code) {
                case ProductUtil.FNOL_LOB_GL:
                    lineName = translator(messages.glLine);
                    break;
                case ProductUtil.FNOL_LOB_CP:
                    lineName = translator(messages.cpLine);
                    break;
                case ProductUtil.FNOL_LOB_IM:
                    lineName = translator(messages.imLine);
                    break;
                case ProductUtil.FNOL_LOB_CR:
                    lineName = translator(messages.crLine);
                    break;
                default:
                    lineName = ''; 
            }
            return {
                code: code,
                name: lineName
            };
        });
    }, [translator]);

    useEffect(() => {
        registerInitialComponentValidation(isSelectedLine);
    }, [isSelectedLine, registerInitialComponentValidation]);

    useEffect(() => {
        const lobCodes = _.map(cppPolicies, (policy) => policy.lobCode_Ext);
        const lines = getLinesDropdown(_.orderBy(lobCodes, ['code'], ['asc']));
        setAvailableLines(lines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkIfBusinessLocationPageVisible = useCallback(() => {
        let nextSteps = wizardSteps.slice(currentStepIndex + 1, wizardSteps.length);
        if (lineSelected !== ProductUtil.FNOL_LOB_IM) {
            if (!steps.find((step) => step.path === '/business-location')) {
                // change from IM line to other line
                changeNextSteps(nextSteps);
            }
        } else if (steps.find((step) => step.path === '/business-location')) {
            // change from other line to IM line
            // IM line doesn't need to show Business Location Page
            nextSteps = nextSteps.filter((step) => step.path !== '/business-location');
            changeNextSteps(nextSteps);
        }
    }, [changeNextSteps, currentStepIndex, lineSelected, steps, wizardSteps])

    useEffect(() => {
        checkIfBusinessLocationPageVisible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = useCallback(() => {
        const policySelected = _.find(cppPolicies, (pol) => pol.lobCode_Ext === lineSelected);
        // line changed
        if (hasLineChanged()) {
            const claimData = {
                lossDate: lossDate,
                policyNumber: policyNumber,
                policy: policySelected,
                policies: cppPolicies
            };
            const claimValue = new Claim(claimData);
            const newClaimVM = viewModelService.create(
                claimValue,
                'cc',
                'wni.edge.capabilities.claim.fnol.dto.WniFnolDTO'
            );
            if (claimNumber) {
                return modalApi.showConfirm({
                    title: messages.wantToChangeLine,
                    message: messages.claimDataEnteredCleared,
                    status: 'warning',
                    icon: 'gw-error-outline'
                }).then((result) => {
                    if (result === 'cancel' || result === 'close') {
                        onLineChange(previousLine);
                        return false;
                    }
                    FNOLService.cancelClaim(claimNumber, authHeader);
                    updateWizardData(newClaimVM);
                    checkIfBusinessLocationPageVisible();
                    return newClaimVM;
                }, _.noop);
            }
            updateWizardData(newClaimVM);
            checkIfBusinessLocationPageVisible();
            return newClaimVM;
        }
        return claimVM;
    }, [FNOLService, authHeader, checkIfBusinessLocationPageVisible, claimNumber, claimVM, cppPolicies, hasLineChanged, lineSelected, lossDate, modalApi, onLineChange, policyNumber, previousLine, updateWizardData, viewModelService]);

    const overrides = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        selectLine: {
            value: lineSelected,
            availableValues: availableLines,
            onValueChange: onLineChange
        }
    };

    const resolvers = {
        resolveCallbackMap: {
        }
    };

    return (    
        <WizardPage
            onNext={onNext}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                onModelChange={updateWizardData}
            />
        </WizardPage>
    );
}

FNOLCPPSelectLinePage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLCPPSelectLinePage));
